/**
 * Mochi for Files — the small shared pieces of the design
 * (weco2/files-mochi/fm-core.jsx): avatar, spinner, markdown-lite, the
 * mascot slot, the floating launcher and the "talk to Mochi" wave button.
 *
 * Where the design draws Mochi as a 2D picture, the mascot and the launcher
 * show the real 3D Mochi (MochiScene). The tiny inline avatars (22–36px, one
 * per message) use the flat drawing of the same creature: a WebGL canvas per
 * chat message would exhaust the browser's WebGL contexts.
 */
import { Component, Fragment, Suspense, lazy, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AudioWaveform, UserPlus } from "lucide-react";

import { CompanionFallback } from "../companionFallback";
import { useAssistantConfig } from "../useAssistantConfig";
import { useFmScope } from "./fmScope";
import "./fm.css";

const MochiScene = lazy(() => import("../MochiScene"));

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

/** Falls back to the flat drawing if the 3D scene can't load or render. */
class SceneBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** The design's `FMAvatar`: Mochi's round head badge. */
export function FMAvatar({ size = 22, ring = false, className }: { size?: number; ring?: boolean; className?: string }) {
  const { avatar_hue } = useAssistantConfig();
  return (
    <span className={cx("fm-ava", ring && "is-ring", className)} style={{ width: size, height: size }} aria-hidden="true">
      <CompanionFallback hue={avatar_hue ?? 250} />
    </span>
  );
}

export function FMSpin({ size = 14, className }: { size?: number; className?: string }) {
  return <span className={cx("fm-spin", className)} style={{ width: size, height: size }} aria-hidden="true" />;
}

/**
 * The design's mascot slot (`FMMascot`), filled with the real 3D Mochi.
 * Purely decorative: the control around it (if any) owns the click.
 */
export function FMScene({ size = 96, className }: { size?: number; className?: string }) {
  const cfg = useAssistantConfig();
  const flat = (
    <span className="block h-full w-full" aria-hidden="true">
      <CompanionFallback hue={cfg.avatar_hue ?? 250} />
    </span>
  );
  return (
    <span className={cx("fm-scene", className)} style={size ? { width: size, height: size } : undefined} aria-hidden="true" data-testid="fm-scene">
      <SceneBoundary fallback={flat}>
        <Suspense fallback={flat}>
          <MochiScene
            decorative
            className="h-full w-full"
            avatar={cfg.avatar}
            palette={cfg.palette}
            hue={cfg.avatar_hue ?? 250}
          />
        </Suspense>
      </SceneBoundary>
    </span>
  );
}

// ---- Markdown-lite (FMMd): paragraphs, "- " bullets, "1. " lists, **bold** ----
function inline(s: string, k: string): ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") && p.length > 4 ? (
      <strong key={`${k}-${i}`}>{p.slice(2, -2)}</strong>
    ) : (
      <Fragment key={`${k}-${i}`}>{p}</Fragment>
    ),
  );
}

export function FMMd({ text }: { text: string }) {
  const blocks = String(text || "")
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((b) => b.replace(/^\n+|\n+$/g, ""))
    .filter((b) => b.trim());
  return (
    <div className="fm-md" dir="auto">
      {blocks.map((b, i) => {
        const lines = b.split("\n");
        if (lines.every((l) => /^\s*[-*•] /.test(l)))
          return (
            <ul key={i}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*[-*•] /, ""), `${i}${j}`)}</li>
              ))}
            </ul>
          );
        if (lines.every((l) => /^\s*\d+[.)] /.test(l)))
          return (
            <ol key={i} start={parseInt(lines[0], 10) || 1}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*\d+[.)] /, ""), `${i}${j}`)}</li>
              ))}
            </ol>
          );
        return (
          <p key={i}>
            {lines.map((l, j) => {
              // A markdown heading reads as a bold line in a chat bubble.
              const h = /^#{1,6}\s+(.*)$/.exec(l);
              return (
                <Fragment key={j}>
                  {j > 0 && <br />}
                  {h ? <strong>{inline(h[1], `${i}${j}h`)}</strong> : inline(l, `${i}${j}`)}
                </Fragment>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

/**
 * The floating Mochi launcher (design `FMLauncher`): the one Mochi presence
 * on a Files-area screen. While it is mounted the global Mochi overlay steps
 * aside (fmScope). `working` spins the violet ring; `open` rings it in blue.
 */
export function FMLauncher({
  onClick,
  working = false,
  open = false,
  label,
  hidden = false,
}: {
  onClick: () => void;
  working?: boolean;
  open?: boolean;
  label?: string;
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const { assistant_name: name } = useAssistantConfig();
  useFmScope(true);
  if (hidden || typeof document === "undefined") return null;
  return createPortal(
    <button
      type="button"
      onClick={onClick}
      aria-label={label ?? t("filesMochi.launcher.open", { defaultValue: "Open {{name}}", name })}
      aria-pressed={open ? true : undefined}
      title={
        working
          ? t("filesMochi.launcher.working", { defaultValue: "{{name}} is working", name })
          : name
      }
      className={cx("fm fm-launch", working && "is-working", open && "is-open")}
      data-testid="fm-launcher"
    >
      <span className="fm-launch-ring" aria-hidden="true" />
      <FMScene size={0} />
    </button>,
    document.body,
  );
}

/** "Talk to Mochi" — opens the voice call (design `FMWaveButton`). */
export function FMWaveButton({ onClick, size = 32, disabled }: { onClick: () => void; size?: number; disabled?: boolean }) {
  const { t } = useTranslation();
  const { assistant_name: name } = useAssistantConfig();
  const label = t("filesMochi.voice.talk", { defaultValue: "Talk to {{name}}", name });
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="fmv-wave disabled:opacity-40"
      style={{ width: size, height: size }}
      aria-label={label}
      title={label}
    >
      <AudioWaveform size={Math.round(size * 0.5)} aria-hidden />
    </button>
  );
}

/**
 * The "Mochi" pill in an editor header (design `FMMochiButton`): Mochi's head
 * in a white disc (a spinner while Mochi works) and the assistant's name.
 * `job` mirrors the design's call shape (`job.working`); `working` wins when both are given.
 */
export function FMMochiButton({
  working,
  open,
  onClick,
  job,
  label,
  className,
}: {
  working?: boolean;
  open?: boolean;
  onClick: () => void;
  job?: { working?: boolean } | null;
  /** Overrides the visible label (defaults to the assistant's name). */
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const { assistant_name: name } = useAssistantConfig();
  const busy = working ?? !!job?.working;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!open}
      aria-label={
        open
          ? t("filesMochi.panel.close", { defaultValue: "Close {{name}}", name })
          : t("filesMochi.launcher.open", { defaultValue: "Open {{name}}", name })
      }
      title={`${name} (Ctrl+J)`}
      className={cx("fm fm-pill fm-pill-blue fm-mochi-cta", open && "is-open", className)}
      data-testid="fm-mochi-cta"
    >
      <span className="fm-mochi-cta-ava">{busy ? <FMSpin size={14} /> : <FMAvatar size={22} />}</span>
      <span className="fm-mochi-cta-l">{label ?? name}</span>
    </button>
  );
}

/**
 * The header "Share" pill (design `FMShareButton`). Presentational only: each
 * editor opens its own real sharing UI (usually inside `FMShareDialog`).
 */
export function FMShareButton({
  onClick,
  label,
  disabled,
  className,
}: {
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx("fm fm-pill fm-pill-white disabled:opacity-50", className)}
    >
      <UserPlus size={15} aria-hidden />
      {label ?? t("filesMochi.share.button", "Share")}
    </button>
  );
}

/** The design's `FMMascot` name for the mascot slot (the real 3D Mochi here). */
export const FMMascot = FMScene;
